import moment from "moment";
import { omit } from "lodash";
import appPages from "../../../appPages";

import Utils, {
  getBaseUrl,
  hasItems,
  isWeb,
  navigate,
} from "../../../helpers/utils";

const DESKTOP_SYNC_KEY = "windows";

const BY_DURATION = 0;
const BY_TIME = 1;
const INTEGRATIONS_SORT = BY_TIME;

// ------------------------------------- GET -------------------------------------
const getCalendars = (controller) => {
  const devices = controller.state?.settings?.devices;

  let googleCalendars = devices["google_calendar"] || [];
  let nativeCalendars = devices["mobile_calendar"] || [];
  let outlookCalendars = devices["outlook"] || [];

  return {
    google: googleCalendars.sort().filter((el, i, a) => i === a.indexOf(el)),
    native: nativeCalendars.sort().filter((el, i, a) => i === a.indexOf(el)),
    outlook: outlookCalendars.sort().filter((el, i, a) => i === a.indexOf(el)),
  };
};

// Integration mapping page
const getIntegrationsMapping = async (controller, timeslot) => {
  const action = {
    action: "integrations_mapping",
  };

  const data = await controller.apiService
    .cardAction(action)
    .then((reply) => reply);
  //console.log(data);
  return data.entries;
};

// Track pages: load timeslot integration details
const getTimeslotIntegrations = async (controller, timeslot) => {
  const action = {
    action: "integrations_info",
    timeslot_id: timeslot.id,
  };

  const data = await controller.apiService
    .cardAction(action)
    .then((reply) => reply);
  //console.log(data);
  if (data && timeslot.time) {
    const now = Utils.nowUserTz(controller);
    const isWithin1H = moment(timeslot.time)
      .add(timeslot.duration + 60, "minutes")
      .isAfter(now);
    if (!isWithin1H) timeslot.hasIntegrationLoaded = true;
  }
  timeslot.integration = data;
  return data;
};

// For details page: All received data section
const getIntegrationDataBySource = async (
  controller,
  source,
  pageLastKnownTime,
  pageLastKnownId,
  count = 50,
) => {
  const action = {
    action: "integrations_history",
    source: source,
    last_known_date: pageLastKnownTime,
    last_known_id: pageLastKnownId,
    count: count,
  };

  const data = await controller.apiService
    .cardAction(action)
    .then((reply) => reply);
  //console.log(data)
  return data ? data.entries : null;
};

const isOldIntegration = (isConnected, lastUpdated) => {
  if (!lastUpdated || !isConnected) return true;

  const now = moment();
  const updateTime = moment(lastUpdated);
  const duration = now.diff(updateTime, "hours");

  return duration > 24;
};

const hasEnabledIntegrations = (controller) => {
  const devices = controller.state?.settings?.devices || {};

  let hasEnabled = false;
  Object.keys(devices).forEach((key) => {
    const data = devices[key] || [];
    data.forEach((d) => {
      if (d.isEnabled) hasEnabled = true;
    });
  });

  return hasEnabled;
};

// ------------------------------------- POST -------------------------------------
const saveIntegrations = (
  controller,
  data,
  callback = () => {},
  onError = () => {},
) => {
  const action = {
    action: "save",
    target: "integrations",
    ...data,
  };

  console.log("Save Integrations");
  controller.cardActionWithCallback(action, callback, onError);
};

const saveIntegrationSource = (
  controller,
  data,
  callback = () => {},
  onError = (err) => console.log(err),
) => {
  const action = {
    action: "save",
    target: "integration_source",
    ...data,
  };

  console.log("Save Integrations");
  controller.apiService.cardAction(action).then(callback).catch(onError);
};

function _filterEvents(events, toExclude) {
  const list = [];
  if (hasItems(events)) {
    events.forEach((e) => {
      //ugly filtering, to refactor once we figure it out what are all the scenarios
      let isFiltered = false;
      if (
        e.source.name === "windows" ||
        e.source.name === "macos" ||
        e.source.name === "linux"
      ) {
        isFiltered = toExclude.includes(e.name);
      }
      if (!isFiltered) {
        list.push(e);
      }
    });
  }
  return list;
}

const groupIntegrations = (integrations = []) => {
  // Group by type
  const categories = {};
  const toExclude = [];
  integrations.forEach((integ) => {
    const key = integ?.source?.name;
    if (key) {
      if (!categories[key]) categories[key] = [];
      categories[key].push(integ);

      if (integ.source.name === "chrome") {
        toExclude.push(integ.name + " - Google Chrome");
      }
    }
  });

  // Max duration integration becomes parent & the rest children
  const result = [];
  Object.keys(categories).forEach((name) => {
    const events = categories[name];
    const list = _filterEvents(events, toExclude);

    if (hasItems(list)) {
      // Do not chunk calendar integrations
      if (name.indexOf("calendar_") > -1) {
        list.forEach((int) => {
          result.push({ ...int, details: int.attendees });
        });
        // Chunk other integrations
      } else {
        list.sort((a, b) => {
          if (INTEGRATIONS_SORT === BY_DURATION) {
            if (a.source?.duration > b.source?.duration) return -1;
            else if (a.source?.duration < b.source?.duration) return 1;
            if (a.source?.startTime > b.source?.startTime) return -1;
            else if (a.source?.startTime < b.source?.startTime) return 1;
          } else {
            if (a.source?.startTime > b.source?.startTime) return 1;
            else if (a.source?.startTime < b.source?.startTime) return -1;
            if (a.source?.duration > b.source?.duration) return -1;
            else if (a.source?.duration < b.source?.duration) return 1;
          }

          if (a.id && b.id) return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
          else if (a.name && b.name)
            return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
          else return 0;
        });
        const integration = list[0];
        const details = list.slice(0);

        result.push({ ...integration, details });
      }
    }
  });

  return result;
};

const stopGathering = async (controller, data) => {
  const action = {
    action: "save",
    target: "integration_data",
    operation: "ignore",
    integrations: data,
  };

  return controller.apiService.cardAction(action);
};

const startGathering = async (controller, integrations) => {
  const action = {
    action: "save",
    target: "integration_data",
    operation: "restore",
    integrations: Array.from(integrations),
  };

  return controller.apiService.cardAction(action);
};

const deleteEntries = async (controller, integrationIds) => {
  const action = {
    action: "save",
    target: "integration_data",
    operation: "delete",
    ids: Array.from(integrationIds),
  };

  return controller.apiService.cardAction(action);
};

const deleteRule = async (controller, rule) => {
  const action = {
    action: "save",
    target: "integration_data",
    operation: "delete-rule",
    ids: [rule.id],
  };

  return controller.apiService.cardAction(action);
};

const editRule = async (controller, rule, elementId, cb = () => {}) => {
  const action = {
    action: "save",
    target: "integration_data",
    operation: "edit-rule",
    ...rule,
    element_id: elementId,
  };

  return controller.apiService.cardAction(action).then(cb);
};

const mapIntegrationToElement = async (
  controller,
  { operator, discriminator, integration, element },
  cb = () => {},
) => {
  const action = {
    action: "save",
    target: "integration_data",
    operation: "map",
    operator,
    discriminator,
    element,
    integration: { ...omit(integration, ["rule", "rules"]) },
  };

  return controller.apiService.cardAction(action).then(cb);
};

const __dummyApiPromise = (action) => {
  console.log("------------------->", action);

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve("test");
    }, 100);
  });
};

// ------------------------------------- ENABLE INTEGRATIONS -------------------------------------
const _onHealthSelected = (controller, item, isActive) => {
  if (isActive) {
    navigate(controller, appPages.integrationsHealth, "changepage", {
      isActive,
    });
  } else {
    const data = {
      [item.value]: true,
      id: item.id,
      isEnabled: true,
    };

    authorizeIntegration(controller, data);
    setTimeout(() => {
      saveIntegrationSource(controller, data, () => {
        controller.getItems(appPages.integrationsHealth);
      });
    }, 1000);
  }
};

const _onCalendarSelected = (controller, item, isActive) => {
  if (isActive) {
    _goToCalendarPage(controller, item);
  } else {
    const data = {
      [item.value]: true,
      id: item.id,
      isEnabled: true,
    };
    authorizeIntegration(controller, data, (type, events, email) => {
      saveIntegrationSource(controller, data, () => {
        _goToCalendarPage(controller, item, email);
      });
    });
  }
};

const _onOutlookCalendarSelected = (controller, item, isActive) => {
  if (isActive) {
    _goToOutlookCalendarPage(controller, item);
  } else {
    const data = {
      [item.value]: true,
      id: item.id,
      isEnabled: true,
    };
    saveIntegrationSource(controller, data, () => {
      _goToOutlookCalendarPage(controller, item);
    });
  }
};

const _goToCalendarPage = (controller, item, email) => {
  const calendars = getCalendars(controller);
  navigate(controller, appPages.integrationsCalendar, "changepage", {
    calendarType: item.value,
    email,
    calendars:
      item.value == "calendar_google"
        ? calendars["google"]
        : calendars["native"],
  });
};

const _goToOutlookCalendarPage = (controller, item) => {
  const calendars = getCalendars(controller);
  navigate(controller, appPages.integrationsOutlook, "changepage", {
    calendarType: item.value,
    calendars: calendars["google"],
  });
};

const toggleIntegration = (controller, item, isActive) => {
  if (
    [
      INTEGRATION_KEYS.desktop,
      INTEGRATION_KEYS.chrome,
      INTEGRATION_KEYS.slack,
    ].includes(item.value)
  ) {
    const { devices } = controller.state?.settings || { devices: {} };
    const deviceKey = ["windows", "linux", "macos"].includes(item.value)
      ? "desktop"
      : item.value;
    const intDevices = devices[deviceKey];

    let allDisconnected = false;
    (intDevices || []).forEach((d) => {
      if (d.isConnected || d.isEnabled) allDisconnected = false;
    });

    return navigate(controller, `integrations_${item.value}`, "changepage", {
      isActive,
      reconnect: allDisconnected,
    });
  }

  // Calendars
  if (item.value.indexOf("calendar_") > -1) {
    return _onCalendarSelected(controller, item, isActive);
  }

  //Outlook
  if (item.value === "outlook") {
    return _onOutlookCalendarSelected(controller, item, isActive);
  }

  // Health
  if (item.value === "health") {
    _onHealthSelected(controller, item, isActive);
  }
};

// ------------------------------------- AUTHORIZE -------------------------------------
const authorizeIntegration = (controller, data, cb = () => {}) => {
  if (data.calendar_google) {
    const integration = controller.services.calendarIntegration;
    integration.authorizeIntegration(controller, "google", cb);
  } else if (data.calendar_native && !isWeb) {
    const integration = controller.services.calendarIntegration;
    integration.authorizeIntegration(controller, "native", cb);
  } else if (data.health && !isWeb) {
    const integration = controller.services.healthIntegration;
    integration.authorizeIntegration(controller, true);
  }
};

const reconnect = (controller, integrationName, title, callback = null) => {
  if (
    [
      INTEGRATION_KEYS.calendar,
      INTEGRATION_KEYS.google,
      INTEGRATION_KEYS.health,
    ].includes(integrationName)
  ) {
    return authorizeIntegration(
      controller,
      { [integrationName]: true },
      (type, events, email) => {
        if (callback) {
          callback(type, events, email);
        } else {
          navigate(controller, appPages.integrationsV2);
        }
      },
    );
  }

  navigate(controller, `integrations_${integrationName}`, "changePage", {
    reconnect: true,
    title: title,
  });
};

// ------------------------------------- RULES -------------------------------------
const getIntegrationRules = (data = {}) => {
  const { website, eventName, channelName, app, title } = data;
  const integrationName = (data.source || {}).name;

  // Use name when coming from Rules page
  const name = data.name || "";

  if (integrationName == INTEGRATION_KEYS.chrome) {
    const rules = [
      {
        name: "matches this URL exactly",
        operator: "exactly",
        text: website,
        discriminator: "website",
        integrationName,
      },
      {
        name: "starts with this URL",
        operator: "startsWith",
        text: website,
        discriminator: "website",
        integrationName,
      },
      {
        name: `is from this website (${website || name})`,
        operator: "contains",
        text: getBaseUrl(website) || name,
        discriminator: "website",
        integrationName,
      },
      {
        name: `matches this title exactly (${title || name})`,
        operator: "matches",
        text: title || name,
        discriminator: "title",
        integrationName,
      },
    ];

    return {
      rules,
      defaultRule: rules[2],
    };
  }

  if (
    integrationName === INTEGRATION_KEYS.google ||
    integrationName === INTEGRATION_KEYS.outlook
  ) {
    const rules = [
      {
        name: "matches this event exactly",
        operator: "exactly",
        text: eventName,
        discriminator: "event",
        integrationName,
      },
    ];

    return {
      rules,
      defaultRule: rules[0],
    };
  }

  if (integrationName === INTEGRATION_KEYS.slack) {
    const rules = [
      {
        name: "is from this channel",
        operator: "exactly",
        text: channelName,
        discriminator: "channel",
        integrationName,
      },
    ];

    return {
      rules,
      defaultRule: rules[0],
    };
  }

  if (isFromDesktop(integrationName)) {
    const rules = [
      {
        name: `is from this window (${data.window || name})`,
        operator: "exactly",
        text: data.window || name,
        discriminator: "window",
        integrationName,
      },
      {
        name: `is from this application (${app || name})`,
        operator: "exactly",
        text: app || name,
        discriminator: "application",
        integrationName,
      },
    ];

    return {
      rules,
      defaultRule: rules[1],
    };
  }

  if (integrationName === INTEGRATION_KEYS.health) {
    const rules = [
      {
        name: `matches this activity (${name})`,
        operator: "exactly",
        text: data.name,
        discriminator: "name",
      },
      {
        name: `is from this category (${data.category || name})`,
        operator: "exactly",
        text: data.category || name,
        discriminator: "category",
      },
    ];
    return {
      rules,
      defaultRule: rules[0],
    };
  }
};

const _isOperatorMatch = (a, b) => {
  return (
    a === b ||
    (a === "match" && b === "exactly") ||
    (b === "match" && a === "exactly")
  );
};

const getRuleOptions = (data = {}) => {
  const { device_type, discriminator, operator, name } = data;
  const integrationName = device_type;

  let rules = [];
  if (integrationName == INTEGRATION_KEYS.chrome) {
    rules = [
      {
        name: "matches this URL exactly",
        operator: "exactly",
        text: data.name,
        discriminator: "website",
        integrationName,
      },
      {
        name: "starts with this URL",
        operator: "startsWith",
        text: data.name,
        discriminator: "website",
        integrationName,
      },
      {
        name: `is from this website (${name || ""})`,
        operator: "contains",
        text: getBaseUrl(name),
        discriminator: "website",
        integrationName,
      },
      {
        name: `matches this title exactly (${name || ""})`,
        operator: "matches",
        text: name,
        discriminator: "title",
        integrationName,
      },
    ];
  }

  if (
    integrationName === INTEGRATION_KEYS.google ||
    integrationName === INTEGRATION_KEYS.outlook
  ) {
    rules = [
      {
        name: "matches this event exactly",
        operator: "exactly",
        text: data.name,
        discriminator: "event",
        integrationName,
      },
    ];
  }

  if (integrationName === INTEGRATION_KEYS.slack) {
    rules = [
      {
        name: "is from this channel",
        operator: "exactly",
        text: data.name,
        discriminator: "channel",
        integrationName,
      },
    ];
  }

  if (isFromDesktop(integrationName)) {
    rules = [
      {
        name: `is from this window (${name || ""})`,
        operator: "exactly",
        text: data.name,
        discriminator: "window",
        integrationName,
      },
      {
        name: `is from this application (${name || ""})`,
        operator: "exactly",
        text: name,
        discriminator: "application",
        integrationName,
      },
    ];
  }

  if (integrationName === INTEGRATION_KEYS.health) {
    rules = [
      {
        name: `matches this activity (${name || ""})`,
        operator: "exactly",
        text: name,
        discriminator: "name",
      },
      {
        name: `is from this category (${data.category || ""})`,
        operator: "exactly",
        text: data.category,
        discriminator: "category",
      },
    ];
  }

  const defaultRule = rules.find(
    (r) =>
      _isOperatorMatch(r.operator, operator) &&
      (discriminator == null || r.discriminator == discriminator),
  );

  if (defaultRule) defaultRule.id = data.id;

  return {
    rules,
    defaultRule,
  };
};

const formatRule = (rule) => {
  // If the paramater is "from this website" do not display full link, just display root (IE, docs.google.com)
  return rule?.name.startsWith("is from this website")
    ? {
        ...rule,
        name: `is from this website (${rule.text || ""})`,
      }
    : rule;
};

isFromDesktop = (sourceName) => {
  return [DESKTOP_SYNC_KEY, "macos", "linux"].includes(sourceName);
};

export const INTEGRATION_KEYS = {
  desktop: DESKTOP_SYNC_KEY,
  google: "calendar_google",
  calendar: "calendar_native",
  chrome: "chrome",
  outlook: "outlook",
  health: "health",
  location: "location",
  slack: "slack",
};

const IntegrationsHelper = {
  authorizeIntegration,
  deleteEntries,
  deleteRule,
  editRule,
  formatRule,
  getCalendars,
  getIntegrationDataBySource,
  getIntegrationsMapping,
  getTimeslotIntegrations,
  getIntegrationRules,
  getRuleOptions,
  groupIntegrations,
  hasEnabledIntegrations,
  isOldIntegration,
  isFromDesktop,
  mapIntegrationToElement,
  reconnect,
  saveIntegrations,
  saveIntegrationSource,
  startGathering,
  stopGathering,
  toggleIntegration,
  DESKTOP_SYNC_KEY,
  INTEGRATION_KEYS,
};

export default IntegrationsHelper;
